import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {
  Alert,
  Autocomplete,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { buildReportOutputFieldOptions } from '@pages/reports/lib/reportEntityCompositeFields';
import {
  reportFilterAutocompleteSlotProps,
  reportFilterControlSx,
} from '@pages/reports/lib/reportFilterControlSx';
import { reportsStore } from '@pages/reports/model/reportsStore';
import type {
  ReportFieldDefinition,
  ReportLogicOperator,
} from '@pages/reports/types/reportApiTypes';
import { getToolbarCircleIconButtonSx } from '@shared/lib/toolbarCircleAddButtonSx';
import { OverflowTooltip } from '@shared/ui/overflow_tooltip/OverflowTooltip';
import type { Values } from '@shared/ui/search_multiple_select';

import { ReportAddVariantDialog } from './ReportAddVariantDialog';
import composeStyles from './ReportComposeModal.module.scss';
import { ReportComposeSection } from './ReportComposeSection';
import { ReportFilterLogicConnector } from './ReportFilterLogicConnector';
import { ReportOutputFilterRow } from './ReportOutputFilterRow';
import pageStyles from './Reports.module.scss';

export function ReportComposeForm() {
  const { t } = useTranslation();
  const theme = useTheme();
  const circleIconSx = getToolbarCircleIconButtonSx(theme);
  const [addVariantDialogOpen, setAddVariantDialogOpen] = useState(false);

  const entities = reportsStore((s) => s.entities);
  const entitiesLoading = reportsStore((s) => s.entitiesLoading);
  const entitiesError = reportsStore((s) => s.entitiesError);
  const selectedEntityName = reportsStore((s) => s.selectedEntityName);
  const metadata = reportsStore((s) => s.metadata);
  const metadataLoading = reportsStore((s) => s.metadataLoading);
  const metadataError = reportsStore((s) => s.metadataError);
  const filterControls = reportsStore((s) => s.filterControls);
  const outputRows = reportsStore((s) => s.outputRows);
  const setSelectedEntityName = reportsStore((s) => s.setSelectedEntityName);
  const loadMetadataForEntity = reportsStore((s) => s.loadMetadataForEntity);
  const addOutputRow = reportsStore((s) => s.addOutputRow);
  const removeOutputRow = reportsStore((s) => s.removeOutputRow);
  const setOutputRowSelectedFields = reportsStore((s) => s.setOutputRowSelectedFields);
  const setOutputRowFilterSelection = reportsStore((s) => s.setOutputRowFilterSelection);
  const setOutputRowNestedEntityFilter = reportsStore((s) => s.setOutputRowNestedEntityFilter);
  const selectedEntity = useMemo(
    () => entities.find((e) => e.entityName === selectedEntityName) ?? null,
    [entities, selectedEntityName],
  );

  const outputFieldOptions = useMemo(() => {
    if (!metadata?.fields?.length) return [];
    return buildReportOutputFieldOptions(metadata, t);
  }, [metadata, t]);

  const fieldMap = useMemo(() => {
    const map = new Map<string, ReportFieldDefinition>();
    for (const field of metadata?.fields ?? []) {
      map.set(field.fieldName, field);
    }
    return map;
  }, [metadata]);

  const groupControls = useMemo(
    () => filterControls.filter((c) => c.id.startsWith('__group_')),
    [filterControls],
  );

  const handleEntityOpen = () => {
    if (!selectedEntityName) return;
    void loadMetadataForEntity(selectedEntityName);
  };

  const handleConfirmAddVariant = useCallback(
    (nextLogicOperator: ReportLogicOperator) => {
      addOutputRow(nextLogicOperator);
    },
    [addOutputRow],
  );

  const showOutputControls = Boolean(metadata && !metadataLoading);
  const showOutputRow = Boolean(selectedEntityName && showOutputControls);

  const renderEntityAutocomplete = () => {
    const entityTitle = selectedEntity?.label || selectedEntity?.entityName || '';

    return (
      <OverflowTooltip title={entityTitle}>
        <Autocomplete
          sx={{ ...reportFilterControlSx, width: '100%', maxWidth: '100%' }}
          slotProps={reportFilterAutocompleteSlotProps}
          options={entities}
          loading={entitiesLoading}
          size="small"
          value={selectedEntity}
          getOptionLabel={(o) => o.label || o.entityName}
          isOptionEqualToValue={(a, b) => a.entityName === b.entityName}
          onChange={(_, value) => {
            setSelectedEntityName(value?.entityName ?? null);
            if (value?.entityName) {
              void loadMetadataForEntity(value.entityName);
            }
          }}
          onOpen={handleEntityOpen}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('reports.entityLabel')}
              placeholder={t('reports.entityPlaceholder')}
              inputProps={{
                ...params.inputProps,
                style: {
                  ...(params.inputProps?.style as React.CSSProperties | undefined),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {entitiesLoading || metadataLoading ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </OverflowTooltip>
    );
  };

  const renderOutputRow = (
    row: (typeof outputRows)[number],
    options: { isPrimaryRow: boolean },
  ) => (
    <ReportOutputFilterRow
      key={row.id}
      row={row}
      variant="modal"
      isPrimaryRow={options.isPrimaryRow}
      metadata={metadata!}
      outputFieldOptions={outputFieldOptions}
      fieldMap={fieldMap}
      groupControls={groupControls}
      showAddButton={false}
      onRequestAddRow={() => setAddVariantDialogOpen(true)}
      onOutputFieldChange={(values) => setOutputRowSelectedFields(row.id, values)}
      onFilterChange={(controlId, values) => setOutputRowFilterSelection(row.id, controlId, values)}
      onNestedFilterChange={(fieldName, patch) =>
        setOutputRowNestedEntityFilter(row.id, fieldName, patch)
      }
    />
  );

  return (
    <>
      {entitiesError ? (
        <Alert severity="error" sx={{ width: '100%', gridColumn: '1 / -1' }}>
          {entitiesError}
        </Alert>
      ) : null}

      <div className={composeStyles.composeTopSlot}>
        <div className={composeStyles.composeTopRow}>
          <div className={composeStyles.composeEntityField}>{renderEntityAutocomplete()}</div>
        </div>
      </div>

      <div className={composeStyles.composeFiltersSlot}>
        {metadataError ? (
          <Alert severity="error" sx={{ width: '100%', mb: 1 }}>
            {metadataError}
          </Alert>
        ) : null}

        {showOutputRow ? (
          <ReportComposeSection
            icon={FilterAltOutlinedIcon}
            iconTone="rose"
            title={t('reports.composeSectionFilters')}
            action={
              <button
                type="button"
                className={composeStyles.addGroupLink}
                onClick={() => setAddVariantDialogOpen(true)}>
                {t('reports.composeAddFilterGroup')}
              </button>
            }>
            <div className={composeStyles.filterGroups}>
              {outputRows.map((row, index) => {
                const isFirstRow = index === 0;
                const canRemoveGroup = !isFirstRow;

                return (
                  <div key={row.id}>
                    {index > 0 ? <ReportFilterLogicConnector /> : null}
                    <div className={composeStyles.filterGroup}>
                      <div className={composeStyles.filterGroupMain}>
                        <div className={composeStyles.filterGroupHead}>
                          <span className={composeStyles.filterGroupLabel}>
                            {t('reports.composeFilterGroup', { number: index + 1 })}
                          </span>
                        </div>
                        <div className={composeStyles.filterGroupFields}>
                          {renderOutputRow(row, { isPrimaryRow: isFirstRow })}
                        </div>
                      </div>
                      {canRemoveGroup ? (
                        <Tooltip title={t('reports.composeRemoveFilter')}>
                          <IconButton
                            type="button"
                            aria-label={t('reports.composeRemoveFilter')}
                            className={`${composeStyles.filterGroupRemoveBtn} ${pageStyles.reportFilterCircleBtn}`}
                            onClick={() => removeOutputRow(row.id)}
                            sx={circleIconSx}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </ReportComposeSection>
        ) : selectedEntityName && metadataLoading ? (
          <Typography variant="body2" color="text.secondary">
            {t('reports.composeLoadingMetadata')}
          </Typography>
        ) : null}
      </div>

      <ReportAddVariantDialog
        open={addVariantDialogOpen}
        onClose={() => setAddVariantDialogOpen(false)}
        onConfirm={handleConfirmAddVariant}
      />
    </>
  );
}
